import axiosV2Instance from './axiosV2Instance';

export interface SessionV2 {
  id: number;
  userId: number;
  siteExecutorId?: number;
  siteId: number;
  typeOfWorks: number;
  timeStart: string;
  timeEnd?: string;
  hours?: number; // null если нет прав
  rate?: number; // null если нет прав
  contractId?: number;
  
  // Связанные данные
  siteName?: string;
  siteClosed: boolean;
  userName?: string;
  userClosed: boolean;
  typeOfWorkName?: string;
  typeOfWorkClosed: boolean;
  typeOfWorkSection?: string;
  
  // Финансовые данные (null если нет прав)
  totalAmount?: number;
  /** Выплачено по зарплате (без премий) */
  paidAmount?: number;
  /** Выплачено премиями */
  bonusPaidAmount?: number;
  /** Начислено компенсаций (net, с учётом корректировок) */
  compensationAmount?: number;
  /** Выплачено компенсаций */
  compensationPaidAmount?: number;
  /** Остаток к выплате по зарплате (премии не учитываются) */
  remainingAmount?: number;
  
  // Флаг доступа к финансовым данным
  hasFinancialAccess: boolean;
  /** Право на редактирование (приходит в GET active sessions, чтобы не делать отдельные запросы permissions/edit) */
  canEdit?: boolean;
}

export interface SessionsV2Filters {
  startDate?: Date;
  endDate?: Date;
  userId?: number;
  siteExecutorId?: number; // Для фильтра по внешним исполнителям
  typeOfWorkId?: number;
  limit?: number;
  /** Сервер подставит userId = текущий пользователь и включит личные финансы */
  myOnly?: boolean;
  paymentStatus?: 'paid' | 'unpaid'; // Клиентский фильтр по статусу оплаты
  isAdditionalWork?: boolean; // Клиентский фильтр по доп.работе
}

export interface SessionsV2Statistics {
  totalSessions: number;
  totalHours: number;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  hasFinancialAccess: boolean;
  uniqueUsers: number;
  uniqueTypeOfWorks: number;
}

export interface SessionTimeSuggestions {
  primaryStartTime: string;
  alternativeStartTimes: string[];
  primaryEndTime: string;
  alternativeEndTimes: string[];
}

export interface CreateSessionV2Request {
  siteExecutorId?: number;
  userId?: number;
  typeOfWorks?: number; // Опциональный, если переданы activities
  timeStart: string;
  timeEnd?: string;
  contractId?: number;
  /**
   * Массив активностей для создания вместе с сменой.
   * Если не указан или пустой, смена создается без активностей (или с автоматической, если указан typeOfWorks).
   */
  activities?: CreateSessionActivityRequest[];
}

export interface UpdateSessionV2Request {
  userId?: number;
  siteExecutorId?: number;
  typeOfWorks?: number; // Опциональный
  timeStart: string;
  timeEnd?: string;
  /** true — закрыть смену (нужен timeEnd). false — оставить открытой (timeEnd не применяется). */
  closeSession?: boolean;
  rate?: number;
  contractId?: number;
  /**
   * Массив активностей для синхронизации.
   * Если указан, все активности смены будут заменены на указанные.
   * Если не указан, активности не изменяются.
   */
  activities?: SessionActivityUpdateRequest[];
}

export interface SessionActivityUpdateRequest {
  /**
   * ID активности. Если не указан или 0 - создается новая активность
   */
  id?: number;
  typeOfWorkId: number;
  timeStart?: string;
  timeEnd?: string;
  allocatedHours?: number;
  sortOrder?: number;
  rate: number;
  contractId?: number;
  comment?: string;
  /** Флаг «доп.работа». При сохранении смены применяется к новой/существующей активности. */
  isAdditionalWork?: boolean;
  /** Причина допа (отдельно от комментария активности). Передаётся при создании/редактировании смены с isAdditionalWork. */
  additionalWorkReason?: string;
}

class SessionsV2Api {
  /**
   * Получить все активные смены объекта (без timeEnd)
   * Требует право SESSION_VIEW_ALL
   */
  async getActiveSiteSessions(siteId: number): Promise<SessionV2[]> {
    try {
      const response = await axiosV2Instance.get<SessionV2[]>(`/sites/${siteId}/sessions/active`);
      return response.data;
    } catch (error) {
      console.error('Error fetching active site sessions:', error);
      throw error;
    }
  }

  /**
   * Получить смены объекта с учётом прав доступа
   */
  async getSiteSessions(siteId: number, filters?: SessionsV2Filters): Promise<SessionV2[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.startDate) {
        params.append('startDate', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        params.append('endDate', filters.endDate.toISOString());
      }
      if (filters?.userId) {
        params.append('userId', filters.userId.toString());
      }
      if (filters?.siteExecutorId) {
        params.append('siteExecutorId', filters.siteExecutorId.toString());
      }
      if (filters?.typeOfWorkId) {
        params.append('typeOfWorkId', filters.typeOfWorkId.toString());
      }
      if (filters?.limit) {
        params.append('limit', filters.limit.toString());
      }
      if (filters?.myOnly) {
        params.append('myOnly', 'true');
      }

      const queryString = params.toString();
      
      const response = await axiosV2Instance.get<SessionV2[]>(`/sites/${siteId}/sessions${queryString ? `?${queryString}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching site sessions:', error);
      throw error;
    }
  }

  /**
   * Получить конкретную смену объекта
   */
  async getSiteSession(siteId: number, sessionId: number): Promise<SessionV2> {
    try {
      const response = await axiosV2Instance.get<SessionV2>(
        `/sites/${siteId}/sessions/${sessionId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching site session:', error);
      throw error;
    }
  }

  /**
   * Получить статистику по сменам объекта
   */
  async getSiteSessionsStatistics(siteId: number): Promise<SessionsV2Statistics> {
    try {
      const response = await axiosV2Instance.get<SessionsV2Statistics>(
        `/sites/${siteId}/sessions/statistics`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching site sessions statistics:', error);
      throw error;
    }
  }

  /**
   * Проверить права на создание смен
   */
  async checkCreatePermission(siteId: number): Promise<{ canCreate: boolean }> {
    try {
      const response = await axiosV2Instance.get<{ canCreate: boolean }>(
        `/sites/${siteId}/sessions/permissions/create`
      );
      return response.data;
    } catch (error) {
      console.error('Error checking create permission:', error);
      throw error;
    }
  }

  /**
   * Проверить права на редактирование смены
   */
  async checkEditPermission(siteId: number, sessionId: number): Promise<{ canEdit: boolean }> {
    try {
      const response = await axiosV2Instance.get<{ canEdit: boolean }>(
        `/sites/${siteId}/sessions/${sessionId}/permissions/edit`
      );
      return response.data;
    } catch (error) {
      console.error('Error checking edit permission:', error);
      throw error;
    }
  }

  /**
   * Создать новую смену
   */
  async createSession(siteId: number, data: CreateSessionV2Request): Promise<{ id: number; activityIds?: number[] }> {
    try {
      console.log(`sessionsV2Api.createSession: создание смены для объекта ${siteId}`, data);
      const response = await axiosV2Instance.post<{ id: number; activityIds?: number[] }>(`/sites/${siteId}/sessions`, data);
      console.log(`sessionsV2Api.createSession: создана смена с ID ${response.data.id}`, response.data.activityIds?.length ? `activityIds: ${response.data.activityIds.length}` : '');
      return response.data;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  /**
   * Получить фотографии комментария к активности.
   */
  async getActivityCommentPhotos(
    siteId: number,
    sessionId: number,
    activityId: number
  ): Promise<AdditionalWorkPhotoMedia[]> {
    const response = await axiosV2Instance.get<AdditionalWorkPhotoMedia[]>(
      `/sites/${siteId}/sessions/${sessionId}/activities/${activityId}/comment/photos`
    );
    return response.data;
  }

  /**
   * Загрузить фотографии к комментарию активности.
   */
  async uploadActivityCommentPhotos(
    siteId: number,
    sessionId: number,
    activityId: number,
    files: File[]
  ): Promise<unknown[]> {
    if (files.length === 0) return [];
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));
    const response = await axiosV2Instance.post<unknown[]>(
      `/sites/${siteId}/sessions/${sessionId}/activities/${activityId}/comment/photos`,
      formData,
      { timeout: 120000, headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  /**
   * Получить фотографии, прикреплённые к пометке «доп.работа» для активности.
   */
  async getAdditionalWorkPhotos(
    siteId: number,
    sessionId: number,
    activityId: number
  ): Promise<AdditionalWorkPhotoMedia[]> {
    const response = await axiosV2Instance.get<AdditionalWorkPhotoMedia[]>(
      `/sites/${siteId}/sessions/${sessionId}/activities/${activityId}/additional-work/photos`
    );
    return response.data;
  }

  /**
   * Загрузить фотографии к пометке «доп.работа» для активности.
   * Файлы лучше сжимать на клиенте (compressImageFiles) перед вызовом; сервер конвертирует в WebP.
   */
  async uploadAdditionalWorkPhotos(
    siteId: number,
    sessionId: number,
    activityId: number,
    files: File[]
  ): Promise<unknown[]> {
    if (files.length === 0) return [];
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));
    const response = await axiosV2Instance.post<unknown[]>(
      `/sites/${siteId}/sessions/${sessionId}/activities/${activityId}/additional-work/photos`,
      formData,
      {
        timeout: 120000,
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  /**
   * Массовое создание смен для нескольких пользователей
   */
  async bulkCreateSessions(siteId: number, data: BulkCreateSessionsV2Request): Promise<BulkCreateSessionsV2Response> {
    try {
      console.log(`sessionsV2Api.bulkCreateSessions: массовое создание смен для объекта ${siteId}`, data);
      const response = await axiosV2Instance.post<BulkCreateSessionsV2Response>(`/sites/${siteId}/sessions/bulk`, data);
      console.log(`sessionsV2Api.bulkCreateSessions: создано ${response.data.successfullyCreated} смен из ${response.data.totalRequested} запрошенных`);
      return response.data;
    } catch (error) {
      console.error('Error bulk creating sessions:', error);
      throw error;
    }
  }

  /**
   * Массовое закрытие смен
   */
  async bulkCloseSessions(siteId: number, data: BulkCloseSessionsV2Request): Promise<BulkCloseSessionsV2Response> {
    try {
      console.log(`sessionsV2Api.bulkCloseSessions: массовое закрытие смен для объекта ${siteId}`, data);
      const response = await axiosV2Instance.post<BulkCloseSessionsV2Response>(`/sites/${siteId}/sessions/bulk-close`, data);
      console.log(`sessionsV2Api.bulkCloseSessions: закрыто ${response.data.successfullyClosed} смен из ${response.data.totalRequested} запрошенных`);
      return response.data;
    } catch (error) {
      console.error('Error bulk closing sessions:', error);
      throw error;
    }
  }

  /**
   * Обновить смену
   */
  async updateSession(siteId: number, sessionId: number, data: UpdateSessionV2Request): Promise<void> {
    try {
      console.log(`sessionsV2Api.updateSession: обновление смены ${sessionId} для объекта ${siteId}`, data);
      console.log(`sessionsV2Api.updateSession: activities в запросе:`, data.activities);
      console.log(`sessionsV2Api.updateSession: activities count:`, data.activities?.length ?? 0);
      const response = await axiosV2Instance.put(`/sites/${siteId}/sessions/${sessionId}`, data);
      console.log(`sessionsV2Api.updateSession: смена ${sessionId} обновлена`, response.data);
    } catch (error) {
      console.error('Error updating session:', error);
      throw error;
    }
  }

  /**
   * Обновить активности своей смены (без SESSION_EDIT) — для карточки «Моя смена».
   * Достаточно быть участником объекта и владельцем смены.
   */
  async updateMySession(siteId: number, sessionId: number, data: UpdateSessionV2Request): Promise<void> {
    try {
      await axiosV2Instance.put(`/sites/${siteId}/sessions/my/${sessionId}`, data);
    } catch (error) {
      console.error('Error updating my session:', error);
      throw error;
    }
  }

  /**
   * Удалить смену
   */
  async deleteSession(siteId: number, sessionId: number): Promise<void> {
    try {
      console.log(`sessionsV2Api.deleteSession: удаление смены ${sessionId} для объекта ${siteId}`);
      await axiosV2Instance.delete(`/sites/${siteId}/sessions/${sessionId}`);
      console.log(`sessionsV2Api.deleteSession: смена ${sessionId} удалена`);
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  }

  /**
   * Получить свою активную смену для объекта
   */
  async getMyActiveSession(siteId: number): Promise<SessionV2 | null> {
    try {
      console.log(`sessionsV2Api.getMyActiveSession: получение своей активной смены для объекта ${siteId}`);
      const response = await axiosV2Instance.get<SessionV2 | null>(`/sites/${siteId}/sessions/my`);
      console.log(`sessionsV2Api.getMyActiveSession: получена смена`, response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching my active session:', error);
      throw error;
    }
  }

  /**
   * Получить свою закрытую смену без активностей для объекта
   * Возвращает первую найденную закрытую смену за указанный период, у которой нет активностей
   */
  async getMyClosedSessionsWithoutActivities(
    siteId: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<SessionV2[]> {
    try {
      const params = new URLSearchParams();
      if (startDate) {
        params.append('startDate', startDate.toISOString());
      }
      if (endDate) {
        params.append('endDate', endDate.toISOString());
      }
      const url = `/sites/${siteId}/sessions/my/closed-without-activities${params.toString() ? '?' + params.toString() : ''}`;
      console.log(`sessionsV2Api.getMyClosedSessionsWithoutActivities: получение закрытых смен без активностей для объекта ${siteId}`);
      const response = await axiosV2Instance.get<SessionV2[]>(url);
      
      const sessions = response.data || [];
      console.log(`sessionsV2Api.getMyClosedSessionsWithoutActivities: получено смен: ${sessions.length}`);
      return sessions;
    } catch (error: any) {
      console.error('Error fetching my closed sessions without activities:', error);
      throw error;
    }
  }

  /**
   * Начать свою смену - требуется только участие в объекте
   */
  async startMySession(siteId: number, data: StartMySessionV2Request): Promise<{ id: number }> {
    try {
      console.log(`sessionsV2Api.startMySession: начало своей смены для объекта ${siteId}`, data);
      const response = await axiosV2Instance.post<{ id: number }>(`/sites/${siteId}/sessions/my/start`, data);
      console.log(`sessionsV2Api.startMySession: смена начата с ID ${response.data.id}`);
      return response.data;
    } catch (error) {
      console.error('Error starting my session:', error);
      throw error;
    }
  }

  /**
   * Завершить свою смену - можно завершить только свою смену
   */
  async endMySession(siteId: number, data: EndMySessionV2Request): Promise<void> {
    try {
      console.log(`sessionsV2Api.endMySession: завершение смены ${data.sessionId} для объекта ${siteId}`, data);
      await axiosV2Instance.put(`/sites/${siteId}/sessions/my/end`, data);
      console.log(`sessionsV2Api.endMySession: смена ${data.sessionId} завершена`);
    } catch (error) {
      console.error('Error ending my session:', error);
      throw error;
    }
  }

  /**
   * Добавить контрольную точку в открытую смену - закрывает текущую активную активность и открывает новую
   */
  async addCheckpointToMySession(siteId: number, sessionId: number): Promise<{ id: number }> {
    try {
      console.log(`sessionsV2Api.addCheckpointToMySession: добавление контрольной точки в смену ${sessionId} для объекта ${siteId}`);
      const response = await axiosV2Instance.post<{ id: number }>(`/sites/${siteId}/sessions/my/${sessionId}/checkpoint`);
      console.log(`sessionsV2Api.addCheckpointToMySession: контрольная точка добавлена с ID ${response.data.id}`);
      return response.data;
    } catch (error) {
      console.error('Error adding checkpoint to my session:', error);
      throw error;
    }
  }

  // ============================================================
  // МЕТОДЫ ДЛЯ РАБОТЫ С SESSION ACTIVITY
  // ============================================================

  /**
   * Получить сессию со всеми активностями
   */
  async getSessionWithActivities(siteId: number, sessionId: number): Promise<SessionWithActivities> {
    try {
      const response = await axiosV2Instance.get<SessionWithActivities>(
        `/sites/${siteId}/sessions/${sessionId}/with-activities`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching session with activities:', error);
      throw error;
    }
  }

  /**
   * Получить все активности сессии
   */
  async getSessionActivities(siteId: number, sessionId: number, typeOfWorkId?: number): Promise<SessionActivity[]> {
    try {
      const params = new URLSearchParams();
      if (typeOfWorkId !== undefined) {
        params.append('typeOfWorkId', typeOfWorkId.toString());
      }
      const url = `/sites/${siteId}/sessions/${sessionId}/activities${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axiosV2Instance.get<SessionActivity[]>(url);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching session activities:', error);
      throw error;
    }
  }

  /**
   * Создать активность в сессии
   */
  async createSessionActivity(
    siteId: number, 
    sessionId: number, 
    data: CreateSessionActivityRequest
  ): Promise<{ id: number }> {
    try {
      console.log('API: Creating activity with data:', JSON.stringify(data, null, 2));
      const response = await axiosV2Instance.post<{ id: number }>(
        `/sites/${siteId}/sessions/${sessionId}/activities`,
        data
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating session activity:', error);
      console.error('Error response data:', error?.response?.data);
      console.error('Error response status:', error?.response?.status);
      throw error;
    }
  }

  /**
   * Обновить активность
   */
  async updateSessionActivity(
    siteId: number,
    sessionId: number,
    activityId: number,
    data: UpdateSessionActivityRequest
  ): Promise<void> {
    try {
      await axiosV2Instance.put(
        `/sites/${siteId}/sessions/${sessionId}/activities/${activityId}`,
        data
      );
    } catch (error) {
      console.error('Error updating session activity:', error);
      throw error;
    }
  }

  /**
   * Удалить активность
   */
  async deleteSessionActivity(siteId: number, sessionId: number, activityId: number): Promise<void> {
    try {
      await axiosV2Instance.delete(
        `/sites/${siteId}/sessions/${sessionId}/activities/${activityId}`
      );
    } catch (error) {
      console.error('Error deleting session activity:', error);
      throw error;
    }
  }

  /**
   * Включить "доп.работа" для активности
   */
  async markActivityAdditionalWork(
    siteId: number,
    sessionId: number,
    activityId: number,
    data?: ToggleAdditionalWorkRequest
  ): Promise<{ id: number }> {
    try {
      const response = await axiosV2Instance.post<{ id: number }>(
        `/sites/${siteId}/sessions/${sessionId}/activities/${activityId}/additional-work`,
        data ?? {}
      );
      return response.data;
    } catch (error) {
      console.error('Error marking activity as additional work:', error);
      throw error;
    }
  }

  /**
   * Выключить "доп.работа" для активности
   */
  async unmarkActivityAdditionalWork(
    siteId: number,
    sessionId: number,
    activityId: number,
    data?: ToggleAdditionalWorkRequest
  ): Promise<void> {
    try {
      await axiosV2Instance.delete(
        `/sites/${siteId}/sessions/${sessionId}/activities/${activityId}/additional-work`,
        { data: data ?? {} }
      );
    } catch (error) {
      console.error('Error unmarking activity as additional work:', error);
      throw error;
    }
  }

  /**
   * История включений/выключений "доп.работа" для активности
   */
  async getActivityAdditionalWorkHistory(
    siteId: number,
    sessionId: number,
    activityId: number
  ): Promise<SessionActivityAdditionalWorkHistoryItem[]> {
    try {
      const response = await axiosV2Instance.get<SessionActivityAdditionalWorkHistoryItem[]>(
        `/sites/${siteId}/sessions/${sessionId}/activities/${activityId}/additional-work/history`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching additional work history:', error);
      throw error;
    }
  }

  /**
   * История изменений смены и активностей в ней (создание, редактирование, удаление)
   */
  async getSessionChangeHistory(
    siteId: number,
    sessionId: number
  ): Promise<SessionChangeHistoryEvent[]> {
    const response = await axiosV2Instance.get<SessionChangeHistoryEvent[]>(
      `/sites/${siteId}/sessions/${sessionId}/change-history`
    );
    return response.data;
  }

  /**
   * Добавить активность к текущей сессии (автоматически закрывает предыдущую)
   */
  async addActivityToCurrentSession(
    siteId: number,
    sessionId: number,
    data: AddActivityToSessionRequest
  ): Promise<{ id: number }> {
    try {
      const response = await axiosV2Instance.post<{ id: number }>(
        `/sites/${siteId}/sessions/${sessionId}/activities/add`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error adding activity to session:', error);
      throw error;
    }
  }

  /**
   * Получить активности для нескольких смен одним запросом (BATCH API)
   * Оптимизировано для множественной оплаты - значительно уменьшает количество запросов
   * Автоматически разбивает большие запросы на батчи по 1000 смен (лимит сервера)
   * @param siteId ID объекта
   * @param sessionIds Массив ID смен (автоматически разбивается на батчи по 1000)
   * @returns Map, где ключ - ID смены, значение - массив активностей
   */
  async getBatchSessionActivities(
    siteId: number,
    sessionIds: number[]
  ): Promise<Map<number, SessionActivity[]>> {
    try {
      console.log(`sessionsV2Api.getBatchSessionActivities: загрузка активностей для ${sessionIds.length} смен`);
      
      // Сервер ограничивает запрос максимум 1000 сменами, разбиваем на батчи
      const BATCH_SIZE = 1000;
      const activitiesMap = new Map<number, SessionActivity[]>();
      
      // Если смен меньше или равно 1000, делаем один запрос
      if (sessionIds.length <= BATCH_SIZE) {
        const response = await axiosV2Instance.post<BatchSessionActivitiesResponse[]>(
          `/sites/${siteId}/sessions/batch-activities`,
          { sessionIds }
        );
        
        response.data.forEach(item => {
          activitiesMap.set(item.sessionId, item.activities);
        });
      } else {
        // Разбиваем на батчи по 1000 смен
        const batches: number[][] = [];
        for (let i = 0; i < sessionIds.length; i += BATCH_SIZE) {
          batches.push(sessionIds.slice(i, i + BATCH_SIZE));
        }
        
        console.log(`sessionsV2Api.getBatchSessionActivities: разбито на ${batches.length} батчей (по ${BATCH_SIZE} смен)`);
        
        // Выполняем запросы параллельно для всех батчей
        const batchPromises = batches.map(async (batch, index) => {
          try {
            const response = await axiosV2Instance.post<BatchSessionActivitiesResponse[]>(
              `/sites/${siteId}/sessions/batch-activities`,
              { sessionIds: batch }
            );
            console.log(`sessionsV2Api.getBatchSessionActivities: батч ${index + 1}/${batches.length} загружен (${batch.length} смен)`);
            return response.data;
          } catch (error) {
            console.error(`Error fetching batch ${index + 1}/${batches.length}:`, error);
            throw error;
          }
        });
        
        const batchResults = await Promise.all(batchPromises);
        
        // Объединяем результаты всех батчей
        batchResults.forEach(batchData => {
          batchData.forEach(item => {
            activitiesMap.set(item.sessionId, item.activities);
          });
        });
      }
      
      console.log(`sessionsV2Api.getBatchSessionActivities: загружены активности для ${activitiesMap.size} смен`);
      return activitiesMap;
    } catch (error) {
      console.error('Error fetching batch session activities:', error);
      throw error;
    }
  }

  /**
   * Получить предложения времени начала и окончания смен для объекта
   * Возвращает топ-3 самых популярных времен на основе последних смен
   */
  async getSessionTimeSuggestions(siteId: number): Promise<SessionTimeSuggestions> {
    try {
      console.log(`sessionsV2Api.getSessionTimeSuggestions: запрос для объекта ${siteId}`);
      const response = await axiosV2Instance.get<SessionTimeSuggestions>(
        `/sites/${siteId}/sessions/time-suggestions`
      );
      console.log(`sessionsV2Api.getSessionTimeSuggestions: получен ответ`, response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching session time suggestions:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    }
  }

  /**
   * Получить сводку по видам работ для объекта (оптимизированный endpoint)
   * Возвращает список видов работ с общей статистикой и деталями по сотрудникам одним запросом
   */
  async getWorkTypesSummary(siteId: number): Promise<SiteWorkTypesSummary> {
    try {
      const response = await axiosV2Instance.get<SiteWorkTypesSummary>(
        `/sites/${siteId}/sessions/work-types-summary`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching work types summary:', error);
      throw error;
    }
  }

  /**
   * Получить сводку по сотрудникам для объекта (оптимизированный endpoint)
   * Возвращает список сотрудников с общей статистикой и деталями по видам работ одним запросом
   */
  async getUsersSummary(siteId: number): Promise<SiteUsersSummary> {
    try {
      const response = await axiosV2Instance.get<SiteUsersSummary>(
        `/sites/${siteId}/sessions/users-summary`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching users summary:', error);
      throw error;
    }
  }
}

export const sessionsV2Api = new SessionsV2Api();

export interface StartMySessionV2Request {
  // Тип работы не указывается при начале смены, будет выбран при завершении
}

export interface EndMySessionV2Request {
  sessionId: number;
  typeOfWorks?: number; // Опциональный - если не указан, смена закрывается без активностей
  contractId?: number;
}

// ============================================================
// ТИПЫ ДЛЯ SESSION ACTIVITY
// ============================================================

export interface SessionActivity {
  id: number;
  sessionId: number;
  typeOfWorkId: number;
  timeStart?: string;
  timeEnd?: string;
  /** Явно указанные часы (режим по часам). Если задано, timeStart/timeEnd могут отсутствовать. */
  allocatedHours?: number;
  sortOrder?: number;
  rate: number;
  contractId?: number;
  comment?: string;
  createdAt?: string;
  
  // Дополнительные поля
  /** Причина допа (отдельно от комментария активности). */
  additionalWorkReason?: string;
  typeOfWorkName?: string;
  typeOfWorkClosed: boolean;
  typeOfWorkSection?: string;
  contractTitle?: string;

  // Флаги
  isAdditionalWork?: boolean;
  
  // Вычисляемые поля
  hours?: number;
  percentage?: number;
  amount?: number;
  
  // Финансовые данные
  /** Выплачено (зарплата) */
  paidAmount?: number;
  /** Выплачено премией */
  bonusPaidAmount?: number;
  /** Начисленная, но не оплаченная премия (по earning bonus этой активности) */
  unpaidBonusAmount?: number;
  remainingAmount?: number;
  hasFinancialAccess: boolean;
}

export interface ToggleAdditionalWorkRequest {
  reason?: string;
}

export interface SessionActivityAdditionalWorkHistoryItem {
  id: number;
  sessionActivityId: number;
  addedAt: string;
  addedBy: number;
  addedReason?: string;
  revokedAt?: string;
  revokedBy?: number;
  revokedReason?: string;
  addedByUserName?: string;
  revokedByUserName?: string;
}

/** Событие истории изменений смены или активности */
export interface SessionChangeHistoryEvent {
  kind: 'Session' | 'Activity';
  sessionActivityId?: number;
  eventType: 'Created' | 'Updated' | 'Deleted';
  occurredAt: string;
  userId: number;
  userName?: string;
  details: SessionChangeHistoryDetail[];
}

export interface SessionChangeHistoryDetail {
  fieldName: string;
  oldValue?: string;
  newValue?: string;
}

/** Медиа фото доп.работы (совместимо с Media из usersApi для getMediaUrl). */
export interface AdditionalWorkPhotoMedia {
  id: number;
  mediaType: string;
  entityType: string;
  entityId: number;
  fileId: string;
  sizes: Array<{ sizeType: string; url: string; width?: number; height?: number; fileSize?: number }>;
  width?: number;
  height?: number;
  fileSize?: number;
  mimeType?: string;
  isActive?: boolean;
  createdAt?: string;
  createdBy?: number;
}

export interface SessionWithActivities {
  session: SessionV2;
  activities: SessionActivity[];
  totalHours: number;
  productiveHours: number;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
}

export interface WorkTypeUserSummary {
  userId: number;
  userName: string;
  userClosed: boolean;
  totalHours: number;
  /** Наработано по ставке (часы × ставка), без премий */
  workAccruedAmount: number;
  /** Сумма начисленных премий по активностям */
  bonusAccruedAmount: number;
  /** Заработано всего (ставка + начисленные премии) */
  totalAmount: number;
  /** Выплачено зарплатой (аллокации к work/correction, строка salary) */
  paidSalaryAmount: number;
  /** Выплачено премиями */
  paidBonusAmount: number;
  /** Всего выплачено (зарплата + премии) */
  paidAmount: number;
  /** Невыплаченный остаток по премиям */
  unpaidBonusAmount: number;
  /** Остаток: заработано всего − всего выплачено */
  remainingAmount: number;
  sessionsCount: number;
  activitiesCount: number;
}

export interface WorkTypeSummary {
  typeOfWorkId: number;
  typeOfWorkName: string;
  typeOfWorkSection: string;
  totalHours: number;
  workAccruedAmount: number;
  bonusAccruedAmount: number;
  totalAmount: number;
  paidSalaryAmount: number;
  paidBonusAmount: number;
  paidAmount: number;
  unpaidBonusAmount: number;
  remainingAmount: number;
  sessionsCount: number;
  activitiesCount: number;
  hasFinancialAccess: boolean;
  users: WorkTypeUserSummary[];
}

export interface SiteWorkTypesSummary {
  siteId: number;
  siteName: string;
  totalHours: number;
  workAccruedAmount: number;
  bonusAccruedAmount: number;
  totalAmount: number;
  paidSalaryAmount: number;
  paidBonusAmount: number;
  paidAmount: number;
  unpaidBonusAmount: number;
  /** Остаток на платежах, ещё не распределённый по активностям (уже включён в paidAmount) */
  unallocatedPaymentAmount: number;
  remainingAmount: number;
  hasFinancialAccess: boolean;
  workTypes: WorkTypeSummary[];
}

export interface UserWorkTypeSummary {
  typeOfWorkId: number;
  typeOfWorkName: string;
  typeOfWorkSection: string;
  totalHours: number;
  workAccruedAmount: number;
  bonusAccruedAmount: number;
  totalAmount: number;
  paidSalaryAmount: number;
  paidBonusAmount: number;
  paidAmount: number;
  unpaidBonusAmount: number;
  remainingAmount: number;
  sessionsCount: number;
  activitiesCount: number;
}

export interface UserSummary {
  userId: number;
  userName: string;
  userClosed: boolean;
  totalHours: number;
  workAccruedAmount: number;
  bonusAccruedAmount: number;
  totalAmount: number;
  paidSalaryAmount: number;
  paidBonusAmount: number;
  paidAmount: number;
  unpaidBonusAmount: number;
  unallocatedPaymentAmount: number;
  remainingAmount: number;
  sessionsCount: number;
  activitiesCount: number;
  hasFinancialAccess: boolean;
  workTypes: UserWorkTypeSummary[];
}

export interface SiteUsersSummary {
  siteId: number;
  siteName: string;
  totalHours: number;
  workAccruedAmount: number;
  bonusAccruedAmount: number;
  totalAmount: number;
  paidSalaryAmount: number;
  paidBonusAmount: number;
  paidAmount: number;
  unpaidBonusAmount: number;
  unallocatedPaymentAmount: number;
  remainingAmount: number;
  hasFinancialAccess: boolean;
  users: UserSummary[];
}

export interface CreateSessionActivityRequest {
  typeOfWorkId: number;
  timeStart?: string;
  timeEnd?: string;
  allocatedHours?: number;
  sortOrder?: number;
  rate: number;
  contractId?: number;
  comment?: string;
  /** Флаг «доп.работа». При создании смены применяется к активности после создания. */
  isAdditionalWork?: boolean;
  /** Причина допа (отдельно от комментария активности). Передаётся при создании смены с isAdditionalWork. */
  additionalWorkReason?: string;
}

export interface UpdateSessionActivityRequest {
  typeOfWorkId: number;
  timeStart?: string;
  timeEnd?: string;
  allocatedHours?: number;
  sortOrder?: number;
  rate: number;
  contractId?: number;
  comment?: string;
}

export interface AddActivityToSessionRequest {
  typeOfWorkId: number;
  rate: number;
  contractId?: number;
}

// ============================================================
// ТИПЫ ДЛЯ BATCH API
// ============================================================

export interface BatchSessionActivitiesResponse {
  sessionId: number;
  activities: SessionActivity[];
}

// ============================================================
// ТИПЫ ДЛЯ МАССОВОГО СОЗДАНИЯ СМЕН
// ============================================================

export interface BulkCreateSessionsV2Request {
  siteExecutorIds?: number[];
  userIds?: number[];
  timeStart: string;
  typeOfWorks?: number;
  rate?: number;
  contractId?: number;
}

export interface BulkCreateSessionsV2Response {
  createdSessionIds: number[];
  totalRequested: number;
  successfullyCreated: number;
  skipped: number;
}

// ============================================================
// ТИПЫ ДЛЯ МАССОВОГО ЗАКРЫТИЯ СМЕН
// ============================================================

export interface BulkCloseSessionsV2Request {
  sessionIds: number[];
  timeEnd: string;
}

export interface BulkCloseSessionsV2Response {
  closedSessionIds: number[];
  totalRequested: number;
  successfullyClosed: number;
  skipped: number;
}